import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSyncTagsQuery } from '@dashboard/api/syncMonitor';
import RulesHelp from '@dashboard/components/SyncMonitor/RulesHelp';
import classes from './TagFilter.module.scss';

const GROUPS = [
  { key: 'role', tags: ['active', 'new', 'historic', 'abandoned'] },
  { key: 'flag', tags: ['historic-sending'] },
  { key: 'freshness', tags: ['fresh', 'stale', 'never'] },
  { key: 'pf', tags: ['pf-fresh', 'pf-semifresh', 'pf-stale', 'pf-never'] },
];

/**
 * Chips that filter every sync view. Tags of the same group are alternatives, tags of different
 * groups must all hold. A group with nothing selected does not restrict.
 */
export default function TagFilter({ value, defaults, onChange }) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });
  const { data: resp } = useSyncTagsQuery();
  const counts = resp?.data?.counts ?? {};
  const rules = resp?.data?.rules ?? {};

  const toggle = (tag) => {
    onChange(
      value.includes(tag) ? value.filter((v) => v !== tag) : [...value, tag],
    );
  };

  const isDefault =
    value.length === defaults.length &&
    defaults.every((d) => value.includes(d));

  return (
    <div className={classes.filter}>
      {GROUPS.map((group) => (
        <div key={group.key} className={classes.group}>
          <span
            className={classes.groupLabel}
            title={t(`helpGroup_${group.key}`)}
          >
            {t(`tagGroup_${group.key}`)}
          </span>
          {group.tags.map((tag) => {
            const selected = value.includes(tag);
            const count = counts[tag]?.suitcases;
            return (
              <button
                key={tag}
                type="button"
                aria-pressed={selected}
                className={`${classes.chip} ${selected ? classes.chipOn : ''}`}
                title={`${t(`tagHint_${tag}`, {
                  minDays: rules.fresh_min_days ?? 5,
                  maxAgeDays: rules.fresh_max_age_days ?? 365,
                  activeMinDays: rules.active_min_days ?? 5,
                  newMaxDays: rules.new_max_days ?? 90,
                  pfFreshFrom: rules.pf_fresh_from ?? '',
                  pfSemifreshDays: rules.pf_semifresh_days ?? 90,
                })}\n\n${t('tagCountTitle', {
                  suitcases: count ?? 0,
                  schools: counts[tag]?.schools ?? 0,
                })}`}
                onClick={() => toggle(tag)}
              >
                {t(`tag_${tag}`)}
                {count !== undefined && (
                  <span className={classes.count}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
      ))}
      <button
        type="button"
        className={classes.reset}
        title={t('tagClearHint')}
        disabled={value.length === 0}
        onClick={() => onChange([])}
      >
        {t('tagClear')}
      </button>
      <button
        type="button"
        className={classes.reset}
        title={t('tagResetHint')}
        disabled={isDefault}
        onClick={() => onChange(defaults)}
      >
        {t('tagReset')}
      </button>
      <RulesHelp />
    </div>
  );
}

TagFilter.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaults: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};
